import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';

interface AppXrCondition {
  type: string;
  status: string;
  reason?: string;
  message?: string;
  lastTransitionTime?: string;
}

interface AppXrResourceRef {
  apiVersion: string;
  kind: string;
  name: string;
  namespace?: string;
}

interface AppXrSpec {
  port?: number;
  size?: string;
  replicas?: number;
  healthCheck?: { path?: string };
  lifecycle?: string;
  sourceRepo?: string;
  description?: string;
  owner?: string;
  args?: string[];
  env?: Array<{ name: string; value: string }>;
  ingress?: {
    host?: string;
    iap?: boolean;
    routes?: Array<{ prefix?: string; iap?: boolean }>;
  };
  image?: { filter?: string; policy?: string };
  crossplane?: { resourceRefs?: AppXrResourceRef[] };
}

interface AppXrStatus {
  resolvedImage?: string;
  appNamespace?: string;
  deployment?: {
    ready?: boolean;
    availableReplicas?: number;
    updatedReplicas?: number;
  };
  httproute?: { accepted?: boolean };
  conditions?: AppXrCondition[];
}

interface AppXrResource {
  // Populated from the fetched object so cards can tell App from Sandbox etc.
  kind?: string;
  apiVersion?: string;
  spec?: AppXrSpec;
  status?: AppXrStatus;
}

interface AppXrContextValue {
  resource: AppXrResource | null;
  conditions: AppXrCondition[];
  error: string | null;
  loading: boolean;
}

const HIDDEN_CONDITION_TYPES = new Set(['Responsive']);

const AppXrContext = createContext<AppXrContextValue>({
  resource: null,
  conditions: [],
  error: null,
  loading: true,
});

export function useAppXrResource() {
  return useContext(AppXrContext);
}

export function AppXrResourceProvider({ children }: { children: ReactNode }) {
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [resource, setResource] = useState<AppXrResource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const annotations = entity.metadata.annotations ?? {};
  const workloadUri = annotations['terasky.backstage.io/custom-workload-uri'];
  const clusterName = (
    annotations['backstage.io/managed-by-location'] ?? ''
  ).split(': ')[1];

  useEffect(() => {
    if (!workloadUri || !clusterName) return undefined;

    const fetchResource = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
        const resp = await fetchApi.fetch(`${baseUrl}/proxy${workloadUri}`, {
          headers: { 'Backstage-Kubernetes-Cluster': clusterName },
        });
        if (!resp.ok) {
          setError(`Failed to fetch: ${resp.status}`);
          return;
        }
        setResource(await resp.json());
      } catch (e: any) {
        setError(e.message);
      }
    };

    fetchResource();
    const interval = setInterval(fetchResource, 30000);
    return () => clearInterval(interval);
  }, [workloadUri, clusterName, fetchApi, discoveryApi]);

  const conditions = (resource?.status?.conditions ?? []).filter(
    c => !HIDDEN_CONDITION_TYPES.has(c.type),
  );

  const loading = !!workloadUri && !resource && !error;

  return (
    <AppXrContext.Provider value={{ resource, conditions, error, loading }}>
      {children}
    </AppXrContext.Provider>
  );
}
